import { Injectable } from '@angular/core';
import { ApiService } from "./api.service";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import {catchError, map, tap} from "rxjs/operators";
import {Address, GroupedStore, Order, ProductInCart, ShipmentInterfaceModel, Store} from "../models";
import {BehaviorSubject, Observable, of} from "rxjs";
import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { NewCartService } from "./newcart.service";
import {NewCart, PaymentRequestDetailsResponse, Root} from "../models/new-cart.model";
import { StoreOrderMeResponse } from "../models/store-order.model";
import {LanguageService} from "./language.service";
import {ConfirmDialogComponent} from "../../modules/shared/components/confirm-dialog/confirm-dialog.component";
import {MatDialog} from "@angular/material/dialog";

@Injectable({
  providedIn: 'root'
})
export class OrdersService {
  private resource = '/storefront/account/orders'; // peticion para /api/v2
  private resource_v1 = '/Orders/StoreOrder/Me';
  private service_orders_resource = `${environment.wallet_api_url}${environment.v1}/Orders/StoreOrder/Me`;
  private service_orders_expired_resource = `${environment.wallet_api_url}${environment.v1}/Orders/StoreOrder/Me/Expired`;
  private service_order_detail_resource = `${environment.wallet_api_url}${environment.v1}/Orders/StoreOrder/Details`;
  private service_order_cancel_resource = `${environment.wallet_api_url}${environment.v1}/Orders/StoreOrder/Cancel`;
  private service_order_revert_resource = `${environment.wallet_api_url}${environment.v1}/Orders/StoreOrder/RevertPlacement`;
  private service_order_repurshase_resource = `${environment.wallet_api_url}${environment.v1}/Orders/StoreOrder/Repurchase`;
  private received_payment_requests = `${environment.wallet_api_url}${environment.v1}/Payment/PaymentRequest/ReceivedPaymentRequests`;
  private sent_payment_requests = `${environment.wallet_api_url}${environment.v1}/Payment/PaymentRequest/RequestedPaymentRequests`;
  private payment_request_resource = `${environment.wallet_api_url}${environment.v1}/Payment/PaymentRequest`;

  paginationSubject: BehaviorSubject<{ count: number, total_count: number, total_pages: number }>;

  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private translateService: TranslateService,
    private http: HttpClient,
    private newCartService: NewCartService,
    private languageService: LanguageService,
    private dialog: MatDialog,
  ) { this.paginationSubject = new BehaviorSubject<{ count: number, total_count: number, total_pages: number }>({ count: 0, total_count: 0, total_pages: 0 }); }

  getServiceApiOrders(page: number, page_size: number, filters?: any): Observable<StoreOrderMeResponse> {
    let httpParams = new HttpParams()
      .set('page', page)
      .set('sorts', '-CreatedAt')
      .set('pageSize', page_size);
    if (filters) httpParams = httpParams.set('date', filters.date);
    return this.http.get<StoreOrderMeResponse>(`${this.service_orders_resource}`, { headers: this.getHeader(), params: httpParams });
  }

  getServiceApiExpiredOrders(page: number, page_size: number, filters?: any): Observable<StoreOrderMeResponse> {
    let httpParams = new HttpParams()
      .set('page', page)
      .set('sorts', '-CreatedAt')
      .set('pageSize', page_size);
    if (filters) httpParams = httpParams.set('date', filters.date);
    return this.http.get<StoreOrderMeResponse>(`${this.service_orders_expired_resource}`, { headers: this.getHeader(), params: httpParams });
  }

  cancelServiceOrder(body: { storeOrderId: string }): Observable<any> {
    return this.http.put(`${this.service_order_cancel_resource}`, body, { headers: this.getHeader() });
  }

  revertServiceOrder(body: { storeOrderId: string }): Observable<any> {
    return this.http.post(`${this.service_order_revert_resource}`, body, { headers: this.getHeader() })
      .pipe(
        tap((response: any) => {
          this.newCartService.saveInSessionAndSubject(<NewCart>response.data.cart);
          this.newCartService.cleanStorage();
          this.newCartService.cleanStorageDeliveryAndPayment();
        })
      );
  }

  repurchaseServiceOrder(body: { storeOrderId: string }): Observable<any> {
    return this.http.post(`${this.service_order_repurshase_resource}`, body, { headers: this.getHeader() })
      .pipe(
        tap((response: any) => {
          if (response.data.cart && response.data.cart.groupedByStoreItems.length > 0) {
            this.newCartService.saveInSessionAndSubject(<NewCart>response.data.cart);
            this.newCartService.cleanStorage();
            this.newCartService.cleanStorageDeliveryAndPayment();
          } else {
            this.dialog.open(ConfirmDialogComponent, {
              disableClose: true,
              data: {
                messageUppercase: false,
                action: of({success: true}),
                hiddenCancelAction: true,
                confirmAction: 'ACCEPT',
                cancelAction: 'CANCEL',
                message: "NO_EXISTING_PRODUCTS_PREVIOUS_PURCHASE",
              }
            })
          }
        })
      );
  }

  getServiceApiOrderDetails(id: string): Observable<any> {
    if (id.includes('?')) {
      id = id.split('?')[0];
    }
    const httpParams = new HttpParams()
      .set('StoreOrderId', id)
    return this.http.get(`${this.service_order_detail_resource}`, { headers: this.getHeader(), params: httpParams })
        .pipe(
            catchError((error) => this.apiService.handleErrors(error, true))
        );
  }

  /**
   * Get headers
   * @private
  */
  private getHeader(): HttpHeaders {
    let httpHeaders = this.authService.getHeader();
    httpHeaders = httpHeaders
      .set('X-K-App', '1')
      // .set('X-Payment-Client', environment.xApiClient)
      .set('Accept-Language', this.languageService.selectedLanguage.value);
    return httpHeaders;
  }

  getReceivedPaymentRequests(page: number, page_size: number, filters?: any): Observable<any> {
    let httpParams = new HttpParams()
      .set('page', page)
      .set('sorts', '-CreatedAt')
      .set('pageSize', page_size);
    if (filters) httpParams = httpParams.set('date', filters.date);
    return this.http.get(`${this.received_payment_requests}`, { headers: this.getHeader(), params: httpParams });
  }

  getSentPaymentRequests(page: number, page_size: number, filters?: any): Observable<any> {
    let httpParams = new HttpParams()
      .set('page', page)
      .set('sorts', '-CreatedAt')
      .set('pageSize', page_size);
    if (filters) httpParams = httpParams.set('date', filters.date);
    return this.http.get(`${this.sent_payment_requests}`, { headers: this.getHeader(), params: httpParams });
  }

  getPaymentRequestData(publicCode: string): Observable<PaymentRequestDetailsResponse> {
    let httpParams = new HttpParams()
      .set('PublicCode', publicCode);

    return this.http.get<PaymentRequestDetailsResponse>(`${this.payment_request_resource}/AvailablePaymentRequests`, { headers: this.getHeader(), params: httpParams });
  }

  cancelPaymentRequest(paymentRequestId: string): Observable<Root> {
    return this.http.post<Root>(`${this.payment_request_resource}/Cancel`, {paymentRequestId}, { headers: this.getHeader() })
      .pipe(
        catchError((error): Observable<never> => this.apiService.handleErrors(error, true))
      );
  }
}
